export interface MasterConfigCategoryOption {
  categoryName: string;
  categoryCode: string;
}

export interface MasterConfigItem {
  id?: string;
  categoryName: string;
  displayName: string;
  filterCode: string;
  categoryCode: string;
  isActive: boolean;
}

export interface MasterConfigGroup {
  categoryCode: string;
  categoryName: string;
  items: MasterConfigItem[];
  activeCount: number;
  total: number;
}

/** Unwrap list payloads (array | { data } | { data: { data } } | { result }). */
export const getMasterConfigSafeList = (
  response: unknown,
): MasterConfigItem[] => {
  if (!response) return [];
  if (Array.isArray(response)) return normalizeItems(response);
  const resp = response as Record<string, unknown>;
  if (Array.isArray(resp.data)) return normalizeItems(resp.data);
  const nestedData = resp.data as Record<string, unknown> | undefined;
  if (nestedData && Array.isArray(nestedData.data))
    return normalizeItems(nestedData.data);
  if (Array.isArray(resp.result)) return normalizeItems(resp.result);
  return [];
};

const normalizeItems = (list: unknown[]): MasterConfigItem[] =>
  (list as Record<string, unknown>[])
    .map((rec) => ({
      id:
        rec.id != null
          ? String(rec.id)
          : rec._id != null
            ? String(rec._id)
            : undefined,
      categoryCode: String(rec.categoryCode ?? ""),
      categoryName: String(rec.categoryName ?? ""),
      displayName: String(rec.displayName ?? rec.filterCode ?? ""),
      filterCode: String(rec.filterCode ?? rec.displayName ?? ""),
      isActive: rec.isActive == null ? true : Boolean(rec.isActive),
    }))
    .filter((item) => item.displayName);

/**
 * Build Figma group cards from categories + grouped endpoint payload.
 * Handles grouped entries ({ categoryCode, filters|configs|items|values|data })
 * or a flat record list (grouped by categoryCode). Unknown shapes → empty items.
 */
export const buildMasterConfigGroups = (
  categories: MasterConfigCategoryOption[],
  groupedResponse: unknown,
): MasterConfigGroup[] => {
  const byCategory = new Map<string, MasterConfigItem[]>();

  const push = (item: MasterConfigItem) => {
    if (!item.categoryCode) return;
    const list = byCategory.get(item.categoryCode) ?? [];
    list.push(item);
    byCategory.set(item.categoryCode, list);
  };

  const rawList = (() => {
    if (!groupedResponse) return [];
    if (Array.isArray(groupedResponse)) return groupedResponse;
    const resp = groupedResponse as Record<string, unknown>;
    if (Array.isArray(resp.data)) return resp.data as unknown[];
    const nested = resp.data as Record<string, unknown> | undefined;
    if (nested && Array.isArray(nested.data)) return nested.data as unknown[];
    return [];
  })();

  rawList.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const rec = entry as Record<string, unknown>;
    // Backend grouped-by-category nests values under `filters`
    // (master-config.service.v2.ts:getGroupedByCategory).
    const nested =
      (Array.isArray(rec.filters) && rec.filters) ||
      (Array.isArray(rec.configs) && rec.configs) ||
      (Array.isArray(rec.items) && rec.items) ||
      (Array.isArray(rec.values) && rec.values) ||
      (Array.isArray(rec.data) && rec.data);
    if (nested) {
      getMasterConfigSafeList({
        data: (nested as unknown[]).map((it) => ({
          ...((it as Record<string, unknown> | null) ?? {}),
          categoryCode:
            (it as Record<string, unknown>)?.categoryCode ?? rec.categoryCode,
          categoryName:
            (it as Record<string, unknown>)?.categoryName ?? rec.categoryName,
        })),
      }).forEach(push);
    } else if (rec.categoryCode || rec.displayName || rec.filterCode) {
      getMasterConfigSafeList([rec]).forEach(push);
    }
  });

  return categories.map((cat) => {
    const items = (byCategory.get(cat.categoryCode) ?? []).slice().sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
    return {
      categoryCode: cat.categoryCode,
      categoryName: cat.categoryName,
      items,
      activeCount: items.filter((i) => i.isActive).length,
      total: items.length,
    };
  });
};

/** Build a backend-safe filterCode slug from the display name. */
export const buildMasterConfigFilterCode = (
  explicitCode: string,
  displayName: string,
): string => {
  const trimmed = explicitCode.trim();
  if (trimmed) return trimmed;
  return displayName.trim().toLowerCase().replace(/\s+/g, "_");
};
